import { ref, shallowRef } from "vue";

const STORAGE_KEY = "sparkle.admin.translation-locale";

function readStoredLocale() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeStoredLocale(locale) {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Storage is unavailable: the choice lasts for this page only.
  }
}

const locale = ref(readStoredLocale());
const generation = ref(0);
// Shallow, so a registered field's refs stay refs.
const fields = shallowRef([]);

/**
 * The page-wide language selection shared by every `sp-translatable-field` on an admin edit page
 * and the `sp-translation-toolbar` in its header. The fields register themselves so the toolbar
 * can list them; the toolbar selects a language and every field follows. The selection is remembered in local storage across pages.
 */
export function useTranslationPageStore() {
  /**
   * Adds a field to the page. Returns the function that removes it again.
   *
   * @param {Object} field
   * @param {String} field.attribute - The translated attribute's name.
   * @param {String} [field.label] - The attribute's human name.
   * @param {import("vue").Ref<Array<{ locale: String, name: String }>>} field.languages - The
   *   languages the field is translated into.
   * @returns {Function}
   */
  function registerField(field) {
    fields.value = [...fields.value, field];

    return function unregisterField() {
      fields.value = fields.value.filter((registered) => registered !== field);
    };
  }

  /**
   * Selects a language for every field on the page. Selecting the current language again
   * re-applies it to a field whose own selector moved elsewhere.
   *
   * @param {String} selected
   */
  function select(selected) {
    locale.value = selected;
    generation.value += 1;
    writeStoredLocale(selected);
  }

  return {
    get fields() {
      return fields.value;
    },
    get locale() {
      return locale.value;
    },
    get generation() {
      return generation.value;
    },
    registerField,
    select,
  };
}

/**
 * Resets the shared state. For specs only.
 */
export function resetTranslationPageStore() {
  locale.value = readStoredLocale();
  generation.value = 0;
  fields.value = [];
}
